import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Route, Routes, useMatch } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { ErrorBoundary } from "react-error-boundary";
import { Auth } from "aws-amplify";
import { RootContext } from "../context/root-provider";
import NavBar from "./navbar";
import Footer from "./footer";
import { FallbackComponents } from "./error-handling";
import AutoLogout, { SESSION_TIME_OUT } from "./auto-logout";
import DropAnythingProvider from "./DropAnything";
import { PUBLIC_LIVE_PATH, useRoutes } from "./routes";
import useMagicKeys from "./magic-keys";
import Identity from "../identity/identity";
import ZoomRedirect from "../pages/ZoomRedirect";
import MobileIdentity from "../identity/mobileIdentity";
import AppBackService from "../services/appback";
import { signInWithZoom } from "../services/zoomSignIn";
import zoomSdk from "@zoom/appssdk";
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

// How long the login screen waits to learn whether this is the Zoom client.
const ZOOM_PROBE_MS = 3000;

// Only what the app actually calls — Zoom review checks least privilege, and
// the Marketplace listing declares the same set.
const ZOOM_CAPABILITIES = [
  "openUrl", // navbar.js — open the web app in the browser
  "getRunningContext", // read from the config() response
  "getMeetingUUID", // StartFyreModal.js, ZoomMeetingPage.js — identify the live meeting
  "getMeetingJoinUrl", // StartFyreModal.js — prefill the join URL
  "getMeetingContext", // StartFyreModal.js, ZoomMeetingPage.js — the meeting's topic names the tapestry (TT-1232) and heads the meeting home (TT-1207)
  "startRTMS", // StartFyreModal.js — start live transcript capture
  // services/zoomMeeting.js — the in-meeting surface (TT-1207), on the Live Tap page:
  "getSupportedJsApis", // ZoomRtmsBar.js — pause/resume/status need Zoom 6.5.5; an older client is asked
  "getRTMSStatus", // ZoomRtmsBar.js — the stream's status, read once on open
  "onRTMSStatusChange", // ZoomRtmsBar.js — the stream's status, as it changes
  "pauseRTMS", // ZoomRtmsBar.js — a gap in the transcript, not an end
  "resumeRTMS", // ZoomRtmsBar.js
  "stopRTMS", // ZoomRtmsBar.js — end the tapestry (Zoom's rtms_stopped closes the doc)
  "expandApp", // ZoomShareButton.js — a share from the sidebar can go out at sidebar size
  "shareApp", // ZoomShareButton.js — show this panel to the meeting, view-only
  "onShareApp", // ZoomShareButton.js — the toggle follows Zoom, including a share ended from Zoom's controls
  "sendMessageToChat", // ShareModal.js, LiveLinkModal.js — post a Tapestry's link or a live link to the meeting chat (F4)
];
// services/zoomSignIn.js — sign in on Zoom's word (TT-1208). Kept apart so the
// app still configures where the listing has not caught up with it.
const ZOOM_SIGN_IN_CAPABILITY = "getAppContext";

//get global zoomSDK
let zoomSdkAlias = window.zoomSdk;
zoomSdkAlias = zoomSdk;

const styles = {
  appStyle: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    textAlign: "center",
    position: "relative",
    backgroundColor: "var(--color-bg-primary)",
    color: "var(--color-text-primary)",
    transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
  },
  bodyStyle: {
    flex: 1,
    minHeight: "100vh",
    fontFamily: "Nunito, sans-serif",
    WebkitFontSmoothing: "antialiased",
    position: "relative",
    overflow: "hidden",
  },
  footerStyle: { position: "absolute", width: "100%", bottom: 0 },
};

const App = () => {
  const [redirect, setRedirect] = useState();
  const [testAccount, setTestAccount] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [zoomAuthorized, setZoomAuthorized] = useState(false);
  const [userTimeout, setUserTimeout] = useState(false);
  const [zoomProbeSettled, setZoomProbeSettled] = useState(false);
  const [unsetMagicKeys, setMagicKeys] = useMagicKeys();
  const [width, setWindowWidth] = useState(0);
  const isMobile = useMemo(() => width <= 899, [width]);
  const isXsMobile = useMemo(() => width <= 353, [width]);
  const { publicRoutes, accountActiveRoutes, testAccountRoutes } = useRoutes();
  // The public live-tapestry viewer (TT-1110) sits above the login gate: the
  // token in the URL is its credential, so it renders for anyone, with none
  // of the signed-in shell (NavBar, auto-logout, identity screens).
  const publicLive = useMatch(PUBLIC_LIVE_PATH);

  let zoomAuthRedirectUrl = "";
  if (window.location.href.indexOf("app.tapestryai.com") > -1) {
    zoomAuthRedirectUrl =
      "https://zoom.us/oauth/authorize?response_type=code&client_id=Lv6lVUlAQXyzazf67ZN1pQ&redirect_uri=https://app.tapestryai.com/signup";
  } else {
    zoomAuthRedirectUrl =
      "https://zoom.us/oauth/authorize?response_type=code&client_id=Di_asFugQ9lREdm882oTA&redirect_uri=https://dev.tapestryai.com/signup";
  }

  let {
    authStatus,
    setAuthStatus,
    loadSession,
    authAttrs,
    setAuthAttrs,
    userInfo,
    setUserInfo,
    authCode,
    setAuthCode,
    setInZoomClient,
    setRunningContext,
  } = useContext(RootContext);

  const handleLogout = useCallback(async () => {
    try {
      await Auth.signOut();
      setAuthCode(null);
      setAuthStatus(false);
      setAuthAttrs(null);
      setUserInfo(null);
      setRedirect(null);
      setZoomAuthorized(false);
      localStorage.removeItem("activeTime");
      unsetMagicKeys();
    } catch (e) {
      console.log("Error in logout: ", e);
    }
  }, [setAuthAttrs, setAuthCode, setAuthStatus, setUserInfo, unsetMagicKeys]);

  useEffect(() => {
    const activeTime = localStorage.getItem("activeTime");
    if (activeTime !== null && dayjs().diff(dayjs(new Date(+activeTime)), "m") > SESSION_TIME_OUT) {
      setUserTimeout(true);
    }
    setUserTimeout(false);
  }, []);

  const updateDimensions = useCallback(() => {
    const width = window.innerWidth;
    setWindowWidth(width);
  }, []);

  useEffect(() => {
    updateDimensions();

    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [updateDimensions]);

  useEffect(() => {
    if (authStatus && userTimeout) {
      handleLogout();
    }
  }, [authStatus, handleLogout, userTimeout]);

  useEffect(() => {
    async function zAuth() {
      if (authCode) {
        console.log("authCode: ", authCode);
        if (
          (authCode == "zoom_init" && window.location.href.indexOf("?code") == -1) ||
          window.location.href.indexOf("?code=zoom_init") > -1
        ) {
          window.location.replace(zoomAuthRedirectUrl);
        } else {
          //TODO check response for success or failure
          await AppBackService.authorize(authAttrs.sub, authCode);
          setZoomAuthorized(true);
          await AppBackService.upsertZoomAuthCode(authAttrs.sub, authCode, true);
        }
      }
      await AppBackService.refreshZoomAuth(authAttrs.sub);
    }
    if (!zoomAuthorized && authAttrs) {
      zAuth();
    }
  }, [authCode, authAttrs, zoomAuthorized, zoomAuthRedirectUrl]);

  useEffect(() => {
    async function linkZoom() {
      const ZoomAuth = await AppBackService.getZoomAuthCode(authAttrs.sub);
      if (ZoomAuth != null && !ZoomAuth.auth?.linked) {
        setAuthCode(ZoomAuth.auth?.code);
      }
    }
    if (authAttrs) {
      linkZoom();
    }
  }, [authAttrs, setAuthCode]);

  useEffect(() => {
    if (testAccount) {
      setMagicKeys({ accountId, username: userInfo?.username }, testAccount);
    }
  }, [testAccount, accountId, setMagicKeys, userInfo?.username]);

  useEffect(() => {
    // The login screen waits for this probe (TT-1208): inside Zoom the user is
    // signed in without it. Outside Zoom config() rejects at once. In some
    // other embedding it may never settle, so the wait is capped.
    const probeCap = setTimeout(() => setZoomProbeSettled(true), ZOOM_PROBE_MS);

    async function configureSdk() {
      try {
        console.log("Initiate SDK configuration.");

        // Configure the JS SDK, required to call JS APIs in the Zoom App
        let configResponse;
        try {
          configResponse = await zoomSdkAlias.config({ capabilities: [...ZOOM_CAPABILITIES, ZOOM_SIGN_IN_CAPABILITY] });
        } catch (error) {
          // If the Marketplace listing does not carry the sign-in capability
          // yet, the app must still work: configure without it. The sign-in
          // below then fails on its own and the login screen shows.
          configResponse = await zoomSdkAlias.config({ capabilities: ZOOM_CAPABILITIES });
        }
        clearTimeout(probeCap);
        console.log("SDK configured", configResponse);

        // The config method returns the running context of the Zoom App
        //window.zoomSdk.setRunningContext(configResponse.runningContext);

        console.log("App Configured: setRunningContext:", configResponse.runningContext);
        setRunningContext(configResponse.runningContext);

        setInZoomClient(true);

        // Zoom knows the user; sign them in to their own account (TT-1208).
        // If it runs past its timeout the login screen shows, and a session
        // that still arrives is loaded then.
        await signInWithZoom(zoomSdkAlias, { onSignedIn: loadSession });
      } catch (error) {
        console.log(error);
        setInZoomClient(false);
      } finally {
        clearTimeout(probeCap);
        setZoomProbeSettled(true);
      }
    }
    configureSdk();
    return () => clearTimeout(probeCap);
  }, [setInZoomClient, setRunningContext, loadSession]);

  const res = useMemo(() => {
    return (
      <div className="App" id="main-content" style={styles.appStyle}>
        <div className="body-content" style={styles.bodyStyle}>
          {publicLive ? (
            <ErrorBoundary FallbackComponent={FallbackComponents}>{publicRoutes()}</ErrorBoundary>
          ) : (
            <>
              {/* Signed in or not, the pages wait for the Zoom probe: what
                  "/" is depends on it (routes.js), and the library would
                  otherwise mount and fetch only to give way (TT-1207). */}
              {(authStatus === null || !zoomProbeSettled) && <CircularProgress size={60} sx={{ marginTop: "40px" }} />}
              {authStatus === true && authAttrs && (
                <>
                  <NavBar authAttrs={authAttrs} logout={handleLogout} mobileState={isMobile} xsWindow={isXsMobile} />
                </>
              )}

              <ErrorBoundary FallbackComponent={FallbackComponents}>
                {authStatus === false && zoomProbeSettled && (
                  <Routes>
                    <Route exact path="/zoom" element={<ZoomRedirect />} />
                    <Route path="*" element={isMobile ? <MobileIdentity /> : <Identity />} />
                  </Routes>
                )}
                {authStatus === true && zoomProbeSettled && (
                  <DropAnythingProvider>
                    <AutoLogout logout={handleLogout} />
                    {!testAccount && <>{authStatus && accountActiveRoutes(redirect)}</>}
                    {testAccount && testAccountRoutes(redirect)}
                  </DropAnythingProvider>
                )}
              </ErrorBoundary>
            </>
          )}
        </div>
        <div style={styles.footerStyle}>
          <Footer />
        </div>
      </div>
    );
  }, [
    publicLive,
    publicRoutes,
    authStatus,
    zoomProbeSettled,
    isMobile,
    authAttrs,
    testAccount,
    isXsMobile,
    redirect,
    handleLogout,
    accountActiveRoutes,
    testAccountRoutes,
  ]);
  return res;
};
export default App;
